import type { CapabilityFlags } from "../types/CapabilityFlags.js";
import type { InternalSupportItem } from "../types/SupportItem.js";
import {
  assertHelpCenterPresent,
  defaultItems,
  HELP_CENTER_ID,
} from "../config/default-items.js";
import { CAPABILITY_REGISTRY } from "../config/capability-registry.js";

// US1944211 — `bb_env` → URL family mapping. The bridge-bar host emits
// `bb_env` verbatim from `config.env` (`bridge-bar/src/api.js:208`), one of
// `{local, dz_np, dz_prod, dev, qa, prod}` or absent. The six values collapse
// 3-to-1 onto two URL families (dangerzone / live). Absent/unknown falls
// back to `live` — mirrors the host's own `|| templatesMap.prod` default in
// `environmentProperties.js:45`. Failing toward `live` is deliberate (the
// authorize.coxautoinc.com endpoint is the canonical destination); release
// confidence in non-prod environments is the launch gate (PO-M-3), NOT a
// merge gate.
// dev/qa intentionally target the live `authorize.coxautoinc.com` endpoint:
// no non-prod `authorize.*` host exists, so the bridge-bar host's own
// `|| templatesMap.prod` default routes there too. Release-confidence in
// non-prod is the PO-M-3 launch gate, NOT a merge gate. Do NOT "correct"
// these to dangerzone — see project-context.md EXCEPTION (US1944211).
const BB_ENV_TO_FAMILY: Record<string, "dangerzone" | "live"> = {
  local: "dangerzone",
  dz_np: "dangerzone",
  dz_prod: "dangerzone",
  dev: "live",
  qa: "live",
  prod: "live",
};

export function resolveEnvHref(
  bbEnv: string | undefined,
  hrefByEnv: Record<string, string> & { live: string },
): string {
  const family =
    bbEnv !== undefined && bbEnv !== "" ? BB_ENV_TO_FAMILY[bbEnv] : undefined;
  return family !== undefined
    ? (hrefByEnv[family] ?? hrefByEnv.live)
    : hrefByEnv.live;
}

// Pure transformation: capability flags → ordered support-menu item list.
// Extracted from App.ts so the flag-bag resolution that satisfies Rally ACs
// #1/#2/#4/#5/#6/#7/#10 is unit-testable without spinning up a custom element.
// Ordering invariant: [non-help-center defaults] → [registry dynamics in
// registry-key order] → [help center]. AC#6: Help Center is always last.
// Fail-closed gate: descriptor must exist AND flag must be strictly === true
// (defense-in-depth — the public API is now individual boolean HTML attributes,
// so the strict gate guards only the programmatic-assignment path).
export function buildFilteredItems(
  capabilities: unknown,
  bbEnv?: string,
): InternalSupportItem[] {
  const flags: CapabilityFlags =
    typeof capabilities === "object" &&
    capabilities !== null &&
    !Array.isArray(capabilities)
      ? (capabilities as CapabilityFlags)
      : {};

  const dynamics: InternalSupportItem[] = [];
  for (const flagName of Object.keys(CAPABILITY_REGISTRY) as Array<
    keyof CapabilityFlags
  >) {
    const descriptor = CAPABILITY_REGISTRY[flagName];
    if (descriptor && flags[flagName] === true) {
      dynamics.push({ ...descriptor });
    }
  }

  const helpCenter = assertHelpCenterPresent(defaultItems);
  const otherDefaults = defaultItems
    .filter((d) => d.id !== HELP_CENTER_ID)
    .map((d) => {
      if (!d.hrefByEnv) return { ...d };
      // Strip `hrefByEnv` from the emitted copy — downstream consumers see a
      // single resolved `href`, per the InternalSupportItem JSDoc contract.
      const { hrefByEnv, ...rest } = d;
      return { ...rest, href: resolveEnvHref(bbEnv, hrefByEnv) };
    });

  return [...otherDefaults, ...dynamics, { ...helpCenter }];
}
